/**
 * Runtime vendor activation status for embed consent observability.
 *
 * Combines the static `VENDORS[]` catalog with:
 *   - Resolved consent categories (from consent_module)
 *   - Per-vendor feature/settings gates
 *   - Initialization probes (DOM/globals) and explicit `markVendorInitialized` calls
 *
 * A vendor is `active` when consent for its category is granted, the vendor is
 * enabled for this page/session, and it has initialized (script loaded / cookie set).
 */

import { Settings } from 'services/settings'

import { VENDORS } from './vendor_catalog'

/**
 * @typedef {'necessary' | 'functional' | 'performance' | 'targeting'} ConsentCategory
 */

/**
 * @typedef {import('./vendor_catalog').VendorKey} VendorKey
 */

/**
 * @typedef {Object} VendorRuntimeContext
 * @property {Record<string, boolean>} [featureFlags] - Feature gates keyed by flag name.
 * @property {typeof defaultProbes | null} [probes] - Override probes; `null` = marks only (tests).
 * @property {typeof defaultEnabledChecks} [enabledChecks] - Override enablement checks (tests).
 */

/**
 * @typedef {Object} VendorRuntimeStatus
 * @property {VendorKey} key
 * @property {string} displayName
 * @property {ConsentCategory} category
 * @property {boolean} consentGranted - Whether the vendor's consent category is granted.
 * @property {boolean} enabled - Whether settings/feature flags allow this vendor on this page.
 * @property {boolean} initialized - Whether the vendor has loaded or set its runtime artifacts.
 * @property {boolean} active - `consentGranted && enabled && initialized`.
 */

/** @type {Set<VendorKey>} */
const initializedVendorKeys = new Set()

/** @type {Set<() => void>} */
const subscribers = new Set()

const notifyVendorRuntimeChange = () => {
  subscribers.forEach((callback) => {
    try {
      callback()
    } catch {
      /* subscriber errors must not break vendor tracking */
    }
  })
}

/**
 * Subscribe to vendor runtime changes (initialization flags, etc.). Fires when
 * `markVendorInitialized` / `markVendorUninitialized` mutate tracked state.
 *
 * @param {() => void} callback
 * @returns {() => void} unsubscribe
 */
export const onVendorRuntimeChange = (callback) => {
  subscribers.add(callback)
  return () => subscribers.delete(callback)
}

/**
 * Record that a vendor has initialized (e.g. script onLoad). Used by script
 * components and tests when DOM/global probes are insufficient or async.
 * @param {VendorKey} key
 */
export const markVendorInitialized = (key) => {
  if (initializedVendorKeys.has(key)) return
  initializedVendorKeys.add(key)
  notifyVendorRuntimeChange()
}

/**
 * Clear a vendor's initialized flag (e.g. on consent revocation teardown).
 * @param {VendorKey} key
 */
export const markVendorUninitialized = (key) => {
  if (!initializedVendorKeys.delete(key)) return
  notifyVendorRuntimeChange()
}

/** Test-only: reset explicit initialization tracking between tests. */
export const resetVendorRuntimeForTests = () => {
  initializedVendorKeys.clear()
  subscribers.clear()
}

const GOOGLE_TAG_MANAGER_URL = 'https://www.googletagmanager.com/gtag/js'

/** @type {Record<VendorKey, () => boolean>} */
const defaultProbes = {
  calendly_core: () => true,
  calendly_feature_flags: () => typeof window.statsig !== 'undefined',
  calendly_anonymous_id: () => /(?:^|;\s*)cal_anonymous_id=/.test(document.cookie),
  calendly_booking_storage: () => /(?:^|;\s*)calendly_invitee_/.test(document.cookie),
  segment: () => Boolean(document.getElementById('analytics-js')),
  segment_targeting: () => /(?:^|;\s*)ajs_user_id=/.test(document.cookie),
  google_analytics: () => Boolean(document.querySelector(`script[src^="${GOOGLE_TAG_MANAGER_URL}"]`)),
  meta_pixel: () => Boolean(document.querySelector('script[src*="connect.facebook.net"]')),
  datadog_rum: () => typeof window.DD_RUM !== 'undefined',
  stripe: () => typeof window.Stripe !== 'undefined',
  paypal: () => typeof window.paypal !== 'undefined',
  recaptcha: () => typeof window.grecaptcha !== 'undefined',
  intercom: () => typeof window.Intercom === 'function',
}

/** @type {Record<VendorKey, (ctx: VendorRuntimeContext) => boolean>} */
const defaultEnabledChecks = {
  segment: () => Settings.segment?.enabled === true,
  meta_pixel: () => Settings.facebook_pixel?.enabled === true,
  calendly_booking_storage: (ctx) => ctx.featureFlags?.growth_booking_data_storage === true,
  calendly_feature_flags: () => Boolean(Settings.statsig?.client_key),
  recaptcha: () => (
    Settings.recaptcha?.activated === true
    || Settings.recaptcha?.booking?.activated === true
  ),
}

const isVendorEnabled = (key, context) => {
  const checks = context.enabledChecks ?? defaultEnabledChecks
  const check = checks[key]
  return check ? check(context) : true
}

const resolveProbes = (context) => (
  context.probes === null ? {} : (context.probes ?? defaultProbes)
)

const isVendorInitialized = (key, context) => {
  const probes = resolveProbes(context)
  const probe = probes[key]
  if (typeof probe === 'function') {
    if (probe()) return true
    // Probe present and negative: DOM/cookie/globals are gone (e.g. script unmounted).
    return false
  }
  return initializedVendorKeys.has(key)
}

/**
 * Compute runtime status for every catalog vendor.
 *
 * @param {Record<ConsentCategory, 'granted' | 'denied'>} categories Resolved consent categories.
 * @param {VendorRuntimeContext} [context] Feature flags and optional probe overrides.
 * @returns {VendorRuntimeStatus[]}
 */
export const getVendorStatuses = (categories, context = {}) => VENDORS.map((vendor) => {
  const consentGranted = categories?.[vendor.category] === 'granted'
  const enabled = isVendorEnabled(vendor.key, context)
  const initialized = isVendorInitialized(vendor.key, context)
  const active = consentGranted && enabled && initialized

  return {
    key: vendor.key,
    displayName: vendor.displayName,
    category: vendor.category,
    consentGranted,
    enabled,
    initialized,
    active,
  }
})
